import BigNumber from 'bignumber.js';

import type { ChainAddressId } from '@infinex/asset-config';
import { makeChainAddressId } from '@infinex/asset-config';
import type { ChainKey } from '@infinex/chain-config';
import type { Rate } from '@infinex/types';

type TokenBalanceForUsd = {
  chain: ChainKey;
  address: string;
  balance: string;
};

export type TokenBalanceWithUSD<T extends TokenBalanceForUsd> = T & {
  balanceUsd?: number;
  usdRate?: number;
};

// The chain/asset pairs we'll accept gas in, inc. some chain wildcards
// Ordered by our _preference_; where possible we default to assets at the top of the list
// This list is temporarily huge before https://github.com/infinex-xyz/platform/pull/13717 lands,
// after which we'll use the DB chain asset flags as the source of truth for support
const SUPPORTED_GAS_DEPOSIT_CHAIN_ASSETS: readonly [ChainKey, string][] = [
  // === Preferred stablecoins & gas tokens (hand-curated priority) ===

  // USDC
  ['optimism', 'usd_coin'],
  ['base', 'usd_coin'],
  ['polygon', 'usd_coin'],
  ['hyperEVM', 'usd_coin'],
  ['arbitrum', 'usd_coin'],
  ['solana', 'usd_coin'],

  // USDt
  ['optimism', 'usd_tether'],
  ['polygon', 'usd_tether'],
  ['solana', 'usd_tether'],

  // sUSD
  ['optimism', 'susd_optimism'],
  ['arbitrum', 'nusd'],

  // USDe
  ['optimism', 'usd_ethena'],
  ['base', 'usd_ethena'],
  ['hyperEVM', 'usd_ethena'],
  ['arbitrum', 'usd_ethena'],
  ['solana', 'usd_ethena'],

  // Wrapped ETH
  ['optimism', 'eth_wrapped'],
  ['base', 'eth'],
  ['arbitrum', 'eth'],

  // SOL
  ['solana', 'sol'],

  // HYPE
  ['hyperEVM', 'hype'],

  // === Remaining price-trusted assets by category ===

  // --- USD Stablecoins ---
  ['optimism', 'alchemix_usd'],
  ['optimism', 'binance_peg_busd'],
  ['optimism', 'crvusd'],
  ['optimism', 'dai'],
  ['optimism', 'dola_usd'],
  ['optimism', 'frax'],
  ['optimism', 'frax_usd'],
  ['optimism', 'liquity_usd'],
  ['optimism', 'susds'],
  ['optimism', 'usd_coin_ethereum_bridged'],
  ['optimism', 'usdt0'],
  ['base', 'agora_dollar'],
  ['base', 'crvusd'],
  ['base', 'dai'],
  ['base', 'dola_usd'],
  ['base', 'frax_usd'],
  ['base', 'gho'],
  ['base', 'l2_standard_bridged_usdt_base'],
  ['base', 'liquity_usd'],
  ['base', 'susds'],
  ['base', 'usd_coin_base_bridged'],
  ['base', 'usds'],
  ['base', 'usual_usd'],
  ['polygon', 'agora_dollar'],
  ['polygon', 'binance_peg_busd'],
  ['polygon', 'crvusd'],
  ['polygon', 'dai'],
  ['polygon', 'frax'],
  ['polygon', 'frax_usd'],
  ['polygon', 'liquity_usd'],
  ['polygon', 'usd_coin_ethereum_bridged'],
  ['hyperEVM', 'usdt0'],
  ['arbitrum', 'agora_dollar'],
  ['arbitrum', 'alchemix_usd'],
  ['arbitrum', 'crvusd'],
  ['arbitrum', 'dai'],
  ['arbitrum', 'dola_usd'],
  ['arbitrum', 'first_digital_usd'],
  ['arbitrum', 'frax'],
  ['arbitrum', 'frax_usd'],
  ['arbitrum', 'gho'],
  ['arbitrum', 'liquity_usd'],
  ['arbitrum', 'paypal_usd'],
  ['arbitrum', 'susds'],
  ['arbitrum', 'usd_coin_ethereum_bridged'],
  ['arbitrum', 'usdd'],
  ['arbitrum', 'usds'],
  ['arbitrum', 'usdt0'],
  ['arbitrum', 'usual_usd'],
  ['ink', 'frax_usd'],
  ['ink', 'gho'],
  ['ink', 'global_dollar'],
  ['ink', 'usd_coin'],
  ['ink', 'usdt0'],
  ['unichain', 'frax_usd'],
  ['unichain', 'usd_coin'],
  ['unichain', 'usdt0'],
  ['monad', 'agora_dollar'],
  ['monad', 'usd_coin'],
  ['monad', 'usd1_wlfi'],
  ['monad', 'usdt0'],
  ['sonic', 'frax_usd'],
  ['sonic', 'sonic_bridged_usdc_e_sonic'],
  ['berachain', 'frax_usd'],
  ['berachain', 'usd_coin_ethereum_bridged'],
  ['berachain', 'usd_ethena'],
  ['berachain', 'usdt0'],
  ['avalanche', 'agora_dollar'],
  ['avalanche', 'binance_peg_busd'],
  ['avalanche', 'frax'],
  ['avalanche', 'frax_usd'],
  ['avalanche', 'gho'],
  ['avalanche', 'true_usd'],
  ['avalanche', 'usd_coin'],
  ['avalanche', 'usd_ethena'],
  ['avalanche', 'usd_tether'],
  ['avalanche', 'usdd'],
  ['bsc', 'agora_dollar'],
  ['bsc', 'binance_bridged_usdc_bnb_smart_chain'],
  ['bsc', 'binance_bridged_usdt_bnb_smart_chain'],
  ['bsc', 'binance_peg_busd'],
  ['bsc', 'binance_peg_dai'],
  ['bsc', 'crvusd'],
  ['bsc', 'dola_usd'],
  ['bsc', 'first_digital_usd'],
  ['bsc', 'frax'],
  ['bsc', 'frax_usd'],
  ['bsc', 'standx_dusd'],
  ['bsc', 'true_usd'],
  ['bsc', 'usd_ethena'],
  ['bsc', 'usd1_wlfi'],
  ['bsc', 'usdd'],
  ['bsc', 'usual_usd'],
  ['solana', 'agora_dollar'],
  ['solana', 'first_digital_usd'],
  ['solana', 'frax'],
  ['solana', 'frax_usd'],
  ['solana', 'global_dollar'],
  ['solana', 'paypal_usd'],
  ['solana', 'standx_dusd'],
  ['solana', 'usd1_wlfi'],
  ['solana', 'usds'],
  ['solana', 'usdtb'],
  ['mantle', 'agora_dollar'],
  ['mantle', 'usd_ethena'],
  ['mantle', 'usd1_wlfi'],
  ['mantle', 'usdt0'],
  ['megaeth', 'megaUsd'],
  ['megaeth', 'usdt0'],
  ['sui', 'agora_dollar'],
  ['sui', 'first_digital_usd'],
  ['sui', 'sui_bridged_usdt_sui'],
  ['sui', 'usd_coin'],
  ['near', 'usd_coin'],
  ['near', 'usd_tether'],
  ['near', 'usdd'],
  ['blast', 'frax_usd'],
  ['blast', 'usd_blast'],
  ['blast', 'usd_ethena'],

  // --- ETH-Pegged ---
  ['optimism', 'coinbase_wrapped_staked_eth'],
  ['optimism', 'eth_lido_staked_wrapped'],
  ['optimism', 'eth_rocket_pool'],
  ['optimism', 'ethena_staked_usde'],
  ['optimism', 'frax_ether'],
  ['optimism', 'kelp_dao_restaked_eth'],
  ['optimism', 'wrapped_eeth'],
  ['base', 'coinbase_wrapped_staked_eth'],
  ['base', 'eth_lido_staked_wrapped'],
  ['base', 'eth_renzo_staked'],
  ['base', 'eth_rocket_pool'],
  ['base', 'ethena_staked_usde'],
  ['base', 'kelp_dao_restaked_eth'],
  ['base', 'wrapped_eeth'],
  ['polygon', 'coinbase_wrapped_staked_eth'],
  ['polygon', 'eth_lido_staked_wrapped'],
  ['polygon', 'eth_rocket_pool'],
  ['polygon', 'frax_ether'],
  ['hyperEVM', 'ethena_staked_usde'],
  ['hyperEVM', 'wrapped_eeth'],
  ['arbitrum', 'coinbase_wrapped_staked_eth'],
  ['arbitrum', 'eth_lido_staked_wrapped'],
  ['arbitrum', 'eth_renzo_staked'],
  ['arbitrum', 'eth_rocket_pool'],
  ['arbitrum', 'ethena_staked_usde'],
  ['arbitrum', 'frax_ether'],
  ['arbitrum', 'kelp_dao_restaked_eth'],
  ['arbitrum', 'stakewise_v3_oseth'],
  ['arbitrum', 'sweth'],
  ['arbitrum', 'wrapped_eeth'],
  ['ink', 'eth_renzo_staked'],
  ['ink', 'kelp_dao_restaked_eth'],
  ['ink', 'wrapped_eeth'],
  ['unichain', 'eth_lido_staked_wrapped'],
  ['unichain', 'eth_renzo_staked'],
  ['unichain', 'eth_rocket_pool'],
  ['unichain', 'kelp_dao_restaked_eth'],
  ['unichain', 'unichain_bridged_weth_unichain'],
  ['unichain', 'wrapped_eeth'],
  ['monad', 'eth_lido_staked_wrapped'],
  ['sonic', 'kelp_dao_restaked_eth'],
  ['sonic', 'wrapped_eeth'],
  ['berachain', 'ethena_staked_usde'],
  ['berachain', 'kelp_dao_restaked_eth'],
  ['berachain', 'l2_standard_bridged_weth_berachain'],
  ['berachain', 'wrapped_eeth'],
  ['avalanche', 'avalanche_bridged_weth_avalanche'],
  ['avalanche', 'ethena_staked_usde'],
  ['avalanche', 'kelp_dao_restaked_eth'],
  ['avalanche', 'wrapped_eeth'],
  ['bsc', 'binance_peg_weth'],
  ['bsc', 'eth_renzo_staked'],
  ['bsc', 'ethena_staked_usde'],
  ['bsc', 'frax_ether'],
  ['bsc', 'wrapped_beacon_eth'],
  ['bsc', 'wrapped_eeth'],
  ['solana', 'ethena_staked_usde'],
  ['mantle', 'ethena_staked_usde'],
  ['mantle', 'kelp_dao_restaked_eth'],
  ['mantle', 'wrapped_ether_mantle_bridge'],
  ['blast', 'eth_renzo_staked'],
  ['blast', 'ethena_staked_usde'],
  ['blast', 'kelp_dao_restaked_eth'],
  ['blast', 'l2_standard_bridged_weth_blast'],
  ['blast', 'wrapped_eeth'],

  // --- BTC-Pegged ---
  ['optimism', 'kraken_wrapped_btc'],
  ['optimism', 'optimism_bridged_wbtc_optimism'],
  ['base', 'coinbase_wrapped_btc'],
  ['base', 'lombard_staked_btc'],
  ['base', 'solv_btc'],
  ['base', 'solv_protocol_solvbtc_bbn'],
  ['base', 'wrapped_bitcoin'],
  ['polygon', 'polygon_bridged_wbtc_polygon_pos'],
  ['polygon', 'solv_btc'],
  ['polygon', 'solv_protocol_solvbtc_bbn'],
  ['hyperEVM', 'solv_btc'],
  ['hyperEVM', 'solv_protocol_solvbtc_bbn'],
  ['arbitrum', 'arbitrum_bridged_wbtc_arbitrum_one'],
  ['arbitrum', 'coinbase_wrapped_btc'],
  ['arbitrum', 'solv_btc'],
  ['arbitrum', 'solv_protocol_solvbtc_bbn'],
  ['ink', 'kraken_wrapped_btc'],
  ['ink', 'solv_btc'],
  ['ink', 'solv_protocol_solvbtc_bbn'],
  ['unichain', 'kraken_wrapped_btc'],
  ['unichain', 'wrapped_bitcoin'],
  ['monad', 'solv_btc'],
  ['monad', 'solv_protocol_solvbtc_bbn'],
  ['monad', 'wrapped_bitcoin'],
  ['sonic', 'lombard_staked_btc'],
  ['sonic', 'solv_btc'],
  ['sonic', 'solv_protocol_solvbtc_bbn'],
  ['sonic', 'wrapped_bitcoin'],
  ['berachain', 'berachain_bridged_wbtc_berachain'],
  ['berachain', 'lombard_staked_btc'],
  ['berachain', 'solv_btc'],
  ['berachain', 'solv_protocol_solvbtc_bbn'],
  ['avalanche', 'solv_btc'],
  ['avalanche', 'solv_protocol_solvbtc_bbn'],
  ['avalanche', 'wrapped_bitcoin'],
  ['bsc', 'binance_bitcoin'],
  ['bsc', 'lombard_staked_btc'],
  ['bsc', 'solv_btc'],
  ['bsc', 'solv_protocol_solvbtc_bbn'],
  ['bsc', 'wrapped_bitcoin'],
  ['solana', 'coinbase_wrapped_btc'],
  ['solana', 'lombard_staked_btc'],
  ['solana', 'okx_wrapped_btc'],
  ['solana', 'solv_btc'],
  ['solana', 'solv_protocol_solvbtc_bbn'],
  ['solana', 'wrapped_bitcoin'],
  ['mantle', 'solv_btc'],
  ['mantle', 'solv_protocol_solvbtc_bbn'],
  ['sui', 'lombard_staked_btc'],
  ['sui', 'okx_wrapped_btc'],
  ['sui', 'sui_bridged_wbtc_sui'],
  ['sui', 'wrapped_bitcoin'],

  // --- SOL-Pegged ---
  ['bsc', 'binance_peg_sol'],
  ['solana', 'bybit_staked_sol'],
  ['solana', 'msol'],
  ['solana', 'sol_blazestake_staked'],
  ['solana', 'sol_jito_staked'],
  ['solana', 'sol_jupiter_staked'],
  ['solana', 'sol_socean_staked'],
  ['solana', 'solayer_staked_sol'],

  // --- Native Gas Tokens ---
  ['optimism', 'eth'],
  ['base', 'eth_wrapped'],
  ['polygon', 'eth_wrapped'],
  ['polygon', 'wmatic'],
  ['hyperEVM', 'wrapped_hype'],
  ['arbitrum', 'eth_wrapped'],
  ['ink', 'eth'],
  ['ink', 'eth_wrapped'],
  ['unichain', 'eth'],
  ['monad', 'eth_wrapped'],
  ['monad', 'mon'],
  ['sonic', 'sonic_ecosystem_token'],
  ['sonic', 'wrapped_sonic'],
  ['berachain', 'bera'],
  ['berachain', 'wrapped_bera'],
  ['avalanche', 'avax'],
  ['avalanche', 'wrapped_avax'],
  ['bsc', 'binancecoin'],
  ['bsc', 'wbnb'],
  ['solana', 'mon'],
  ['solana', 'wrapped_solana'],
  ['solana', 'wrapped_sonic'],
  ['megaeth', 'eth'],
  ['fogo', 'fogo'],
  ['blast', 'eth'],

  // --- INX ---
  ['bsc', 'infinex'],
  ['solana', 'infinex'],

  // --- Liquid Tokens ---
  ['optimism', 'aave'],
  ['optimism', 'autonolas'],
  ['optimism', 'balancer'],
  ['optimism', 'chainlink'],
  ['optimism', 'curve_dao'],
  ['optimism', 'dai_savings'],
  ['optimism', 'derive'],
  ['optimism', 'ethena'],
  ['optimism', 'fbomb'],
  ['optimism', 'layerzero'],
  ['optimism', 'mountain_protocol_usdm'],
  ['optimism', 'olympus'],
  ['optimism', 'op'],
  ['optimism', 'pendle'],
  ['optimism', 'pyth_network'],
  ['optimism', 'shapeshift_fox_token'],
  ['optimism', 'staked_frax_ether'],
  ['optimism', 'synthetix'],
  ['optimism', 'tbtc'],
  ['optimism', 'the_doge_nft'],
  ['optimism', 'uniswap'],
  ['optimism', 'universal_btc'],
  ['optimism', 'velodrome'],
  ['optimism', 'worldcoin_wld'],
  ['optimism', 'yearn_finance'],
  ['optimism', 'yneth_max'],
  ['base', '1inch'],
  ['base', 'aave'],
  ['base', 'aavegotchi'],
  ['base', 'adshares'],
  ['base', 'aerodrome'],
  ['base', 'aixbt'],
  ['base', 'autonolas'],
  ['base', 'balancer'],
  ['base', 'bankercoin_2'],
  ['base', 'beam'],
  ['base', 'bedrock_btc'],
  ['base', 'bio_protocol'],
  ['base', 'brett'],
  ['base', 'chain_2'],
  ['base', 'chainlink'],
  ['base', 'compounding_open_dollar'],
  ['base', 'cow_protocol'],
  ['base', 'curve_dao'],
  ['base', 'dai_savings'],
  ['base', 'debtreliefbot'],
  ['base', 'defi_ssi'],
  ['base', 'definitive'],
  ['base', 'derive'],
  ['base', 'doginme'],
  ['base', 'eigenlayer'],
  ['base', 'electronic_usd'],
  ['base', 'ethena'],
  ['base', 'ether_fi'],
  ['base', 'euler'],
  ['base', 'euro_coin'],
  ['base', 'fbomb'],
  ['base', 'flayer'],
  ['base', 'freysa_ai'],
  ['base', 'game_by_virtuals'],
  ['base', 'harry_potter_obama_sonic_10_inu'],
  ['base', 'hunt_token'],
  ['base', 'ignition_fbtc'],
  ['base', 'instadapp'],
  ['base', 'kaito'],
  ['base', 'keeta'],
  ['base', 'layerzero'],
  ['base', 'liquid_staked_ethereum'],
  ['base', 'luna_by_virtuals'],
  ['base', 'mag7_ssi'],
  ['base', 'mog_coin'],
  ['base', 'moltbook'],
  ['base', 'moonwell_artemis'],
  ['base', 'morpheusai'],
  ['base', 'morpho'],
  ['base', 'mountain_protocol_usdm'],
  ['base', 'neiro_3'],
  ['base', 'non_playable_coin'],
  ['base', 'odos'],
  ['base', 'olympus'],
  ['base', 'paal_ai'],
  ['base', 'pancakeswap_token'],
  ['base', 'pendle'],
  ['base', 'ponke'],
  ['base', 'pyth_network'],
  ['base', 're_protocol_reusd'],
  ['base', 'rekt_4'],
  ['base', 'renzo'],
  ['base', 'resolv_wstusr'],
  ['base', 'ribbita_by_virtuals'],
  ['base', 'robo_token_2'],
  ['base', 'spx_6900'],
  ['base', 'superfarm'],
  ['base', 'sushi'],
  ['base', 'synthetix'],
  ['base', 'syrup'],
  ['base', 'syrupusdc'],
  ['base', 'tbtc'],
  ['base', 'the_doge_nft'],
  ['base', 'tokenbot_2'],
  ['base', 'toshi'],
  ['base', 'tree_capital'],
  ['base', 'uniswap'],
  ['base', 'unit_00_rei'],
  ['base', 'universal_btc'],
  ['base', 'ussi'],
  ['base', 'veil_token'],
  ['base', 'venice_token'],
  ['base', 'virtual_protocol'],
  ['base', 'web_3_dollar'],
  ['base', 'woo_network'],
  ['base', 'world_mobile_token'],
  ['base', 'wrapped_bcoin'],
  ['base', 'yearn_finance'],
  ['base', 'yield_guild_games'],
  ['base', 'yneth_max'],
  ['base', 'zencash'],
  ['base', 'zora'],
  ['polygon', '1inch'],
  ['polygon', 'aave'],
  ['polygon', 'aavegotchi'],
  ['polygon', 'adshares'],
  ['polygon', 'autonolas'],
  ['polygon', 'balancer'],
  ['polygon', 'bonk'],
  ['polygon', 'chainlink'],
  ['polygon', 'cow_protocol'],
  ['polygon', 'curve_dao'],
  ['polygon', 'dogelon_mars'],
  ['polygon', 'everipedia'],
  ['polygon', 'frax_share'],
  ['polygon', 'instadapp'],
  ['polygon', 'layerzero'],
  ['polygon', 'maker'],
  ['polygon', 'mimatic'],
  ['polygon', 'mountain_protocol_usdm'],
  ['polygon', 'polygon_ecosystem_token'],
  ['polygon', 'render_token'],
  ['polygon', 'shapeshift_fox_token'],
  ['polygon', 'staked_frax_ether'],
  ['polygon', 'stepn'],
  ['polygon', 'superfarm'],
  ['polygon', 'sushi'],
  ['polygon', 'synthetix'],
  ['polygon', 'tbtc'],
  ['polygon', 'tether_gold_tokens'],
  ['polygon', 'the_doge_nft'],
  ['polygon', 'uniswap'],
  ['polygon', 'woo_network'],
  ['polygon', 'wrapped_bcoin'],
  ['polygon', 'yearn_finance'],
  ['polygon', 'yield_guild_games'],
  ['hyperEVM', 'bedrock_btc'],
  ['hyperEVM', 'chainlink'],
  ['hyperEVM', 'kinetic_staked_hype'],
  ['hyperEVM', 'looped_hype'],
  ['hyperEVM', 'mantle_restaked_eth'],
  ['hyperEVM', 'neiro_3'],
  ['hyperEVM', 'pendle'],
  ['hyperEVM', 'pudgy_penguins'],
  ['hyperEVM', 'rekt_4'],
  ['hyperEVM', 'resolv_wstusr'],
  ['hyperEVM', 'tether_gold_tokens'],
  ['hyperEVM', 'unit_bitcoin'],
  ['hyperEVM', 'unit_ethereum'],
  ['hyperEVM', 'universal_btc'],
  ['hyperEVM', 'usdh'],
  ['arbitrum', 'aave'],
  ['arbitrum', 'apex_token_2'],
  ['arbitrum', 'arb'],
  ['arbitrum', 'autonolas'],
  ['arbitrum', 'badger_dao'],
  ['arbitrum', 'balancer'],
  ['arbitrum', 'beincom'],
  ['arbitrum', 'bonk'],
  ['arbitrum', 'chainlink'],
  ['arbitrum', 'circle_xstock'],
  ['arbitrum', 'cow_protocol'],
  ['arbitrum', 'curve_dao'],
  ['arbitrum', 'derive'],
  ['arbitrum', 'electronic_usd'],
  ['arbitrum', 'ethena'],
  ['arbitrum', 'ether_fi'],
  ['arbitrum', 'euler'],
  ['arbitrum', 'evervalue_coin'],
  ['arbitrum', 'fbomb'],
  ['arbitrum', 'frax_share'],
  ['arbitrum', 'gmx'],
  ['arbitrum', 'hegic'],
  ['arbitrum', 'ignition_fbtc'],
  ['arbitrum', 'instadapp'],
  ['arbitrum', 'kleros'],
  ['arbitrum', 'layerzero'],
  ['arbitrum', 'liquity'],
  ['arbitrum', 'mobox'],
  ['arbitrum', 'morpheusai'],
  ['arbitrum', 'morpho'],
  ['arbitrum', 'mountain_protocol_usdm'],
  ['arbitrum', 'olympus'],
  ['arbitrum', 'ondo_us_dollar_yield'],
  ['arbitrum', 'pancakeswap_token'],
  ['arbitrum', 'pendle'],
  ['arbitrum', 'pepe'],
  ['arbitrum', 'pleasing_gold'],
  ['arbitrum', 'pleasing_usd'],
  ['arbitrum', 'pyth_network'],
  ['arbitrum', 're_protocol_reusd'],
  ['arbitrum', 'reserve_protocol_eth_plus'],
  ['arbitrum', 'resolv_wstusr'],
  ['arbitrum', 'shapeshift_fox_token'],
  ['arbitrum', 'spell_token'],
  ['arbitrum', 'staked_frax_ether'],
  ['arbitrum', 'staked_frax_usd'],
  ['arbitrum', 'susdai'],
  ['arbitrum', 'sushi'],
  ['arbitrum', 'syrupusdc'],
  ['arbitrum', 'tbtc'],
  ['arbitrum', 'tesla_xstock'],
  ['arbitrum', 'tether_gold_tokens'],
  ['arbitrum', 'the_doge_nft'],
  ['arbitrum', 'tokenlon'],
  ['arbitrum', 'uniswap'],
  ['arbitrum', 'universal_btc'],
  ['arbitrum', 'universal_eth'],
  ['arbitrum', 'usdai'],
  ['arbitrum', 'vision_3'],
  ['arbitrum', 'woo_network'],
  ['arbitrum', 'world_mobile_token'],
  ['arbitrum', 'wrappedm_by_m0'],
  ['arbitrum', 'yearn_finance'],
  ['arbitrum', 'yneth_max'],
  ['ink', 'bedrock_btc'],
  ['ink', 'chainlink'],
  ['ink', 're_protocol_reusd'],
  ['ink', 'tether_gold_tokens'],
  ['ink', 'universal_btc'],
  ['unichain', 'bedrock_btc'],
  ['unichain', 'bonk'],
  ['unichain', 'chainlink'],
  ['unichain', 'dog_wif_hat'],
  ['unichain', 'euler'],
  ['unichain', 'jupiter'],
  ['unichain', 'uniswap'],
  ['unichain', 'universal_btc'],
  ['monad', 'tether_gold_tokens'],
  ['sonic', 'beets_staked_sonic'],
  ['sonic', 'chainlink'],
  ['sonic', 'ignition_fbtc'],
  ['sonic', 'pendle'],
  ['sonic', 'staked_frax_usd'],
  ['sonic', 'universal_btc'],
  ['berachain', 'bedrock_btc'],
  ['berachain', 'chainlink'],
  ['berachain', 'dolomite'],
  ['berachain', 'euler'],
  ['berachain', 'harry_potter_obama_sonic_10_inu'],
  ['berachain', 'honey'],
  ['berachain', 'olympus'],
  ['berachain', 'pendle'],
  ['berachain', 'resolv_wstusr'],
  ['berachain', 'universal_btc'],
  ['berachain', 'yneth_max'],
  ['avalanche', '1inch'],
  ['avalanche', 'aave'],
  ['avalanche', 'avant_staked_usd'],
  ['avalanche', 'avant_usd'],
  ['avalanche', 'beam'],
  ['avalanche', 'binance_peg_dogecoin'],
  ['avalanche', 'bitcoin_avalanche_bridged_btc_b'],
  ['avalanche', 'blackhole'],
  ['avalanche', 'chainlink'],
  ['avalanche', 'ethena'],
  ['avalanche', 'euler'],
  ['avalanche', 'euro_coin'],
  ['avalanche', 'frax_share'],
  ['avalanche', 'gmx'],
  ['avalanche', 'gunz'],
  ['avalanche', 'kite_2'],
  ['avalanche', 'layerzero'],
  ['avalanche', 'maker'],
  ['avalanche', 'mog_coin'],
  ['avalanche', 'mountain_protocol_usdm'],
  ['avalanche', 'pepe'],
  ['avalanche', 're_protocol_reusd'],
  ['avalanche', 'rekt_4'],
  ['avalanche', 'spx_6900'],
  ['avalanche', 'superfarm'],
  ['avalanche', 'sushi'],
  ['avalanche', 'synthetix'],
  ['avalanche', 'tether_gold_tokens'],
  ['avalanche', 'uniswap'],
  ['avalanche', 'woo_network'],
  ['avalanche', 'yearn_finance'],
  ['bsc', '1inch'],
  ['bsc', 'aave'],
  ['bsc', 'adshares'],
  ['bsc', 'aegis_yusd'],
  ['bsc', 'aioz_network'],
  ['bsc', 'apollox_2'],
  ['bsc', 'apro'],
  ['bsc', 'aster_2'],
  ['bsc', 'baby_doge_coin'],
  ['bsc', 'banana_for_scale_2'],
  ['bsc', 'beam'],
  ['bsc', 'bedrock_btc'],
  ['bsc', 'bianrensheng'],
  ['bsc', 'binance_peg_cardano'],
  ['bsc', 'binance_peg_dogecoin'],
  ['bsc', 'binance_peg_xrp'],
  ['bsc', 'bio_protocol'],
  ['bsc', 'bnb48_club_token'],
  ['bsc', 'bonk'],
  ['bsc', 'boundless'],
  ['bsc', 'brett'],
  ['bsc', 'build_on'],
  ['bsc', 'burnedfi'],
  ['bsc', 'chain_2'],
  ['bsc', 'chainlink'],
  ['bsc', 'chainopera_ai'],
  ['bsc', 'changenow'],
  ['bsc', 'cheems_token'],
  ['bsc', 'circle_xstock'],
  ['bsc', 'coco_2'],
  ['bsc', 'compounding_open_dollar'],
  ['bsc', 'conscious_token'],
  ['bsc', 'cow_protocol'],
  ['bsc', 'cross_2'],
  ['bsc', 'dogelon_mars'],
  ['bsc', 'egl1'],
  ['bsc', 'euler'],
  ['bsc', 'everipedia'],
  ['bsc', 'falcon_finance'],
  ['bsc', 'falcon_finance_ff'],
  ['bsc', 'fbomb'],
  ['bsc', 'floki'],
  ['bsc', 'frax_share'],
  ['bsc', 'giggle_fund'],
  ['bsc', 'htx_dao'],
  ['bsc', 'huma_finance'],
  ['bsc', 'humanity'],
  ['bsc', 'ignition_fbtc'],
  ['bsc', 'ishares_silver_trust_ondo_tokenized_stock'],
  ['bsc', 'kite_2'],
  ['bsc', 'koma_inu'],
  ['bsc', 'layerzero'],
  ['bsc', 'metahero'],
  ['bsc', 'metaplex'],
  ['bsc', 'minidoge_5'],
  ['bsc', 'mobox'],
  ['bsc', 'non_playable_coin'],
  ['bsc', 'paal_ai'],
  ['bsc', 'pancakeswap_token'],
  ['bsc', 'pendle'],
  ['bsc', 'pepe'],
  ['bsc', 'pudgy_penguins'],
  ['bsc', 'radio_caca'],
  ['bsc', 're_protocol_reusd'],
  ['bsc', 'rekt_4'],
  ['bsc', 'resolv_wstusr'],
  ['bsc', 'robo_token_2'],
  ['bsc', 'safepal'],
  ['bsc', 'sentismai'],
  ['bsc', 'simon_s_cat'],
  ['bsc', 'siren_2'],
  ['bsc', 'skyai'],
  ['bsc', 'stable_2'],
  ['bsc', 'staked_frax_ether'],
  ['bsc', 'stbl'],
  ['bsc', 'stepn'],
  ['bsc', 'sushi'],
  ['bsc', 'tagger'],
  ['bsc', 'tesla_xstock'],
  ['bsc', 'the_doge_nft'],
  ['bsc', 'thena'],
  ['bsc', 'tokenfi'],
  ['bsc', 'tornado_cash'],
  ['bsc', 'toshi'],
  ['bsc', 'tradoor'],
  ['bsc', 'tria'],
  ['bsc', 'trust_wallet_token'],
  ['bsc', 'tutorial'],
  ['bsc', 'unibase'],
  ['bsc', 'uniswap'],
  ['bsc', 'universal_btc'],
  ['bsc', 'usdu'],
  ['bsc', 'useless_3'],
  ['bsc', 'why'],
  ['bsc', 'woo_network'],
  ['bsc', 'world_liberty_financial'],
  ['bsc', 'world_mobile_token'],
  ['bsc', 'world_of_dypians'],
  ['bsc', 'yield_guild_games'],
  ['bsc', 'yneth_max'],
  ['bsc', 'yooldo_games'],
  ['bsc', 'zeus_network'],
  ['solana', 'act_i_the_ai_prophecy'],
  ['solana', 'ai_rig_complex'],
  ['solana', 'aixbt'],
  ['solana', 'alchemist_ai'],
  ['solana', 'aura_on_sol'],
  ['solana', 'autonolas'],
  ['solana', 'avici'],
  ['solana', 'baby_doge_coin'],
  ['solana', 'bc_token'],
  ['solana', 'bedrock_btc'],
  ['solana', 'bio_protocol'],
  ['solana', 'bitget_staked_sol'],
  ['solana', 'bonk'],
  ['solana', 'book_of_meme'],
  ['solana', 'buttcoin_7'],
  ['solana', 'cat_in_a_dogs_world'],
  ['solana', 'chainlink'],
  ['solana', 'chill_guy'],
  ['solana', 'circle_xstock'],
  ['solana', 'comedian'],
  ['solana', 'debridge'],
  ['solana', 'dog_wif_hat'],
  ['solana', 'dogelon_mars'],
  ['solana', 'doublezero'],
  ['solana', 'drift_staked_sol'],
  ['solana', 'ethereum_wormhole'],
  ['solana', 'euro_coin'],
  ['solana', 'fartcoin'],
  ['solana', 'frax_share'],
  ['solana', 'fwog'],
  ['solana', 'gigachad_2'],
  ['solana', 'gme'],
  ['solana', 'goatseus_maximus'],
  ['solana', 'griffain'],
  ['solana', 'gunz'],
  ['solana', 'harry_potter_obama_sonic_10_inu'],
  ['solana', 'hastra_prime'],
  ['solana', 'hastra_wrapped_ylds'],
  ['solana', 'helium'],
  ['solana', 'huma_finance'],
  ['solana', 'instadapp'],
  ['solana', 'ishares_silver_trust_ondo_tokenized_stock'],
  ['solana', 'jelly_my_jelly'],
  ['solana', 'jupiter'],
  ['solana', 'jupiter_perps_lp'],
  ['solana', 'lido_staked_sol'],
  ['solana', 'luna_by_virtuals'],
  ['solana', 'maneki'],
  ['solana', 'melania_meme'],
  ['solana', 'meta_2_2'],
  ['solana', 'metaplex'],
  ['solana', 'metaverse'],
  ['solana', 'meteora'],
  ['solana', 'moo_deng'],
  ['solana', 'moonbirds'],
  ['solana', 'non_playable_coin'],
  ['solana', 'official_fo'],
  ['solana', 'official_trump'],
  ['solana', 'olympus'],
  ['solana', 'ondo_us_dollar_yield'],
  ['solana', 'onyc'],
  ['solana', 'orca'],
  ['solana', 'pain'],
  ['solana', 'pancakeswap_token'],
  ['solana', 'payfi_strategy_token_usdc'],
  ['solana', 'peanut_the_squirrel'],
  ['solana', 'pepecoin_2'],
  ['solana', 'phantom_staked_sol'],
  ['solana', 'pippin'],
  ['solana', 'ponke'],
  ['solana', 'popcat'],
  ['solana', 'pudgy_penguins'],
  ['solana', 'pump_fun'],
  ['solana', 'punch_2'],
  ['solana', 'pyth_network'],
  ['solana', 'pythia'],
  ['solana', 'raydium'],
  ['solana', 'rekt_4'],
  ['solana', 'render_token'],
  ['solana', 'renzo'],
  ['solana', 'seeker'],
  ['solana', 'simon_s_cat'],
  ['solana', 'slerf'],
  ['solana', 'societe_generale_forge_eurcv'],
  ['solana', 'sol_helius_staked'],
  ['solana', 'spx_6900'],
  ['solana', 'stepn'],
  ['solana', 'stonks'],
  ['solana', 'stronghold_staked_sol'],
  ['solana', 'sushi'],
  ['solana', 'swarms'],
  ['solana', 'swissborg'],
  ['solana', 'syrupusdc'],
  ['solana', 'tbtc'],
  ['solana', 'tdccp'],
  ['solana', 'tesla_xstock'],
  ['solana', 'tether_gold_tokens'],
  ['solana', 'the_doge_nft'],
  ['solana', 'the_white_whale'],
  ['solana', 'the9bit'],
  ['solana', 'troll_2'],
  ['solana', 'unicorn_fart_dust'],
  ['solana', 'universal_btc'],
  ['solana', 'usdu'],
  ['solana', 'useless_3'],
  ['solana', 'usx'],
  ['solana', 'vine'],
  ['solana', 'virtual_protocol'],
  ['solana', 'woo_network'],
  ['solana', 'world_liberty_financial'],
  ['solana', 'world_mobile_token'],
  ['solana', 'would'],
  ['solana', 'wrapped_zenbtc'],
  ['solana', 'wrappedm_by_m0'],
  ['solana', 'yzy_money'],
  ['solana', 'zerebro'],
  ['solana', 'zeus_network'],
  ['mantle', 'chainlink'],
  ['mantle', 'ethena'],
  ['mantle', 'ignition_fbtc'],
  ['mantle', 'mantle'],
  ['mantle', 'mantle_restaked_eth'],
  ['mantle', 'mantle_staked_ether'],
  ['mantle', 'ondo_us_dollar_yield'],
  ['mantle', 'tesla_xstock'],
  ['mantle', 'universal_btc'],
  ['megaeth', 'mega-btc-b'],
  ['sui', 'deep'],
  ['sui', 'haedal_staked_sui'],
  ['sui', 'ondo_us_dollar_yield'],
  ['sui', 'tbtc'],
  ['sui', 'walrus_2'],
  ['near', 'aave'],
  ['near', 'chainlink'],
  ['near', 'sushi'],
  ['near', 'synthetix'],
  ['near', 'uniswap'],
  ['near', 'yearn_finance'],
  ['blast', 'chainlink'],
  ['blast', 'ethena'],
  ['blast', 'yneth_max'],

  // Ultimately, accept on Ethereum if we must (still preferencing stablecoins)
  ['ethereum', 'agora_dollar'],
  ['ethereum', 'alchemix_usd'],
  ['ethereum', 'crvusd'],
  ['ethereum', 'dai'],
  ['ethereum', 'dola_usd'],
  ['ethereum', 'first_digital_usd'],
  ['ethereum', 'frax'],
  ['ethereum', 'frax_usd'],
  ['ethereum', 'gho'],
  ['ethereum', 'global_dollar'],
  ['ethereum', 'liquity_usd'],
  ['ethereum', 'paypal_usd'],
  ['ethereum', 'ripple_usd'],
  ['ethereum', 'susds'],
  ['ethereum', 'true_usd'],
  ['ethereum', 'usd_coin'],
  ['ethereum', 'usd_ethena'],
  ['ethereum', 'usd_tether'],
  ['ethereum', 'usd1_wlfi'],
  ['ethereum', 'usdd'],
  ['ethereum', 'usds'],
  ['ethereum', 'usdtb'],
  ['ethereum', 'usual_usd'],
  // ETH-Pegged
  ['ethereum', 'coinbase_wrapped_staked_eth'],
  ['ethereum', 'eth_lido_staked'],
  ['ethereum', 'eth_lido_staked_wrapped'],
  ['ethereum', 'eth_renzo_staked'],
  ['ethereum', 'eth_rocket_pool'],
  ['ethereum', 'ethena_staked_usde'],
  ['ethereum', 'frax_ether'],
  ['ethereum', 'kelp_dao_restaked_eth'],
  ['ethereum', 'stader_ethx'],
  ['ethereum', 'stakewise_v3_oseth'],
  ['ethereum', 'sweth'],
  ['ethereum', 'wrapped_beacon_eth'],
  ['ethereum', 'wrapped_eeth'],
  // BTC-Pegged
  ['ethereum', 'binance_wrapped_btc'],
  ['ethereum', 'coinbase_wrapped_btc'],
  ['ethereum', 'ether_fi_staked_btc'],
  ['ethereum', 'kraken_wrapped_btc'],
  ['ethereum', 'lombard_staked_btc'],
  ['ethereum', 'solv_btc'],
  ['ethereum', 'solv_protocol_solvbtc_bbn'],
  ['ethereum', 'wrapped_bitcoin'],
  // Native Gas Tokens
  ['ethereum', 'eth'],
  ['ethereum', 'eth_wrapped'],
  // INX
  ['ethereum', 'infinex'],
  // Liquid Tokens
  ['ethereum', '0x0_ai_ai_smart_contract'],
  ['ethereum', '1inch'],
  ['ethereum', 'a_hunters_dream'],
  ['ethereum', 'aave'],
  ['ethereum', 'aavegotchi'],
  ['ethereum', 'adshares'],
  ['ethereum', 'aegis_yusd'],
  ['ethereum', 'aioz_network'],
  ['ethereum', 'aixbt'],
  ['ethereum', 'alchemix'],
  ['ethereum', 'altered_state_token'],
  ['ethereum', 'andy_the_wisguy'],
  ['ethereum', 'apex_token_2'],
  ['ethereum', 'apro'],
  ['ethereum', 'apu_s_club'],
  ['ethereum', 'arb'],
  ['ethereum', 'atoshi'],
  ['ethereum', 'autonolas'],
  ['ethereum', 'aztec'],
  ['ethereum', 'baby_doge_coin'],
  ['ethereum', 'badger_dao'],
  ['ethereum', 'balancer'],
  ['ethereum', 'banana_gun'],
  ['ethereum', 'beam'],
  ['ethereum', 'bedrock_btc'],
  ['ethereum', 'bio_protocol'],
  ['ethereum', 'block_4'],
  ['ethereum', 'bone_shibaswap'],
  ['ethereum', 'bonk'],
  ['ethereum', 'boundless'],
  ['ethereum', 'chain_2'],
  ['ethereum', 'chainlink'],
  ['ethereum', 'changenow'],
  ['ethereum', 'circle_xstock'],
  ['ethereum', 'compounding_open_dollar'],
  ['ethereum', 'constitutiondao'],
  ['ethereum', 'cow_protocol'],
  ['ethereum', 'curve_dao'],
  ['ethereum', 'dai_savings'],
  ['ethereum', 'degate'],
  ['ethereum', 'derive'],
  ['ethereum', 'dogelon_mars'],
  ['ethereum', 'dolomite'],
  ['ethereum', 'eigenlayer'],
  ['ethereum', 'electronic_usd'],
  ['ethereum', 'eth_dinero_staked_yield'],
  ['ethereum', 'ethena'],
  ['ethereum', 'ethena_staked_ena'],
  ['ethereum', 'ether_fi'],
  ['ethereum', 'ether_fi_staked_eth'],
  ['ethereum', 'euler'],
  ['ethereum', 'euro_coin'],
  ['ethereum', 'everipedia'],
  ['ethereum', 'f_x_protocol_fxusd'],
  ['ethereum', 'falcon_finance'],
  ['ethereum', 'falcon_finance_ff'],
  ['ethereum', 'fidelity_digital_dollar'],
  ['ethereum', 'flayer'],
  ['ethereum', 'floki'],
  ['ethereum', 'foom'],
  ['ethereum', 'frankencoin'],
  ['ethereum', 'frax_share'],
  ['ethereum', 'fuel_network'],
  ['ethereum', 'fx_usd_saving'],
  ['ethereum', 'game_stop'],
  ['ethereum', 'harry_potter_obama_sonic_10_inu'],
  ['ethereum', 'hegic'],
  ['ethereum', 'htx_dao'],
  ['ethereum', 'humanity'],
  ['ethereum', 'hunt_token'],
  ['ethereum', 'ignition_fbtc'],
  ['ethereum', 'illuvium'],
  ['ethereum', 'infinifi_usd'],
  ['ethereum', 'instadapp'],
  ['ethereum', 'ishares_silver_trust_ondo_tokenized_stock'],
  ['ethereum', 'joe_coin'],
  ['ethereum', 'kekius_maximus'],
  ['ethereum', 'kite_2'],
  ['ethereum', 'kleros'],
  ['ethereum', 'layerzero'],
  ['ethereum', 'liquid_staked_ethereum'],
  ['ethereum', 'liquity'],
  ['ethereum', 'maker'],
  ['ethereum', 'mantle'],
  ['ethereum', 'mantle_restaked_eth'],
  ['ethereum', 'mantle_staked_ether'],
  ['ethereum', 'mbg_by_multibank_group'],
  ['ethereum', 'metamask_usd'],
  ['ethereum', 'milady_cult_coin'],
  ['ethereum', 'mog_coin'],
  ['ethereum', 'morpheusai'],
  ['ethereum', 'morpho'],
  ['ethereum', 'mountain_protocol_usdm'],
  ['ethereum', 'neiro_3'],
  ['ethereum', 'non_playable_coin'],
  ['ethereum', 'noon_usn'],
  ['ethereum', 'nusd'],
  ['ethereum', 'nusd_2'],
  ['ethereum', 'olympus'],
  ['ethereum', 'ondo_finance'],
  ['ethereum', 'ondo_us_dollar_yield'],
  ['ethereum', 'openserv'],
  ['ethereum', 'origin_ether'],
  ['ethereum', 'paal_ai'],
  ['ethereum', 'pancakeswap_token'],
  ['ethereum', 'pendle'],
  ['ethereum', 'pepe'],
  ['ethereum', 'pepecoin_2'],
  ['ethereum', 'polygon_ecosystem_token'],
  ['ethereum', 'precious_metals_usd'],
  ['ethereum', 'punkstrategy'],
  ['ethereum', 'pyth_network'],
  ['ethereum', 'radicle'],
  ['ethereum', 'radio_caca'],
  ['ethereum', 'railgun'],
  ['ethereum', 're_protocol_reusd'],
  ['ethereum', 'rekt_4'],
  ['ethereum', 'render_token'],
  ['ethereum', 'renzo'],
  ['ethereum', 'reserve_protocol_eth_plus'],
  ['ethereum', 'resolv_wstusr'],
  ['ethereum', 'restaked_swell_eth'],
  ['ethereum', 'resupply_usd'],
  ['ethereum', 'robo_token_2'],
  ['ethereum', 'rollbit_coin'],
  ['ethereum', 'safepal'],
  ['ethereum', 'savings_crvusd'],
  ['ethereum', 'shapeshift_fox_token'],
  ['ethereum', 'shiba_inu'],
  ['ethereum', 'shuffle_2'],
  ['ethereum', 'sky'],
  ['ethereum', 'societe_generale_forge_eurcv'],
  ['ethereum', 'spell_token'],
  ['ethereum', 'spx_6900'],
  ['ethereum', 'staked_frax_ether'],
  ['ethereum', 'staked_frax_usd'],
  ['ethereum', 'stepn'],
  ['ethereum', 'stronghold_token'],
  ['ethereum', 'superfarm'],
  ['ethereum', 'sushi'],
  ['ethereum', 'swissborg'],
  ['ethereum', 'synthetix'],
  ['ethereum', 'syrup'],
  ['ethereum', 'syrupusdc'],
  ['ethereum', 'syrupusdt'],
  ['ethereum', 'tbtc'],
  ['ethereum', 'tesla_xstock'],
  ['ethereum', 'tether_gold'],
  ['ethereum', 'the_doge_nft'],
  ['ethereum', 'tokenfi'],
  ['ethereum', 'tokenlon'],
  ['ethereum', 'tornado_cash'],
  ['ethereum', 'tree_capital'],
  ['ethereum', 'tria'],
  ['ethereum', 'undeads_games'],
  ['ethereum', 'unibase'],
  ['ethereum', 'uniswap'],
  ['ethereum', 'universal_btc'],
  ['ethereum', 'universal_eth'],
  ['ethereum', 'usd0_liquid_bond'],
  ['ethereum', 'usdkg'],
  ['ethereum', 'vestra_dao'],
  ['ethereum', 'virtual_protocol'],
  ['ethereum', 'vision_3'],
  ['ethereum', 'web_3_dollar'],
  ['ethereum', 'woo_network'],
  ['ethereum', 'world_liberty_financial'],
  ['ethereum', 'world_mobile_token'],
  ['ethereum', 'worldcoin_wld'],
  ['ethereum', 'wrapped_bcoin'],
  ['ethereum', 'wrapped_nxm'],
  ['ethereum', 'wrappedm_by_m0'],
  ['ethereum', 'yearn_finance'],
  ['ethereum', 'yield_guild_games'],
  ['ethereum', 'yneth_max'],
] as const;

// Sort and filter the supplied asset balances based on our preferred chains and assets
export function getPreferredGasAccountAssets<
  T extends {
    id: ChainAddressId;
    chain: ChainKey;
    assetId?: string | undefined;
    isOfficial: boolean;
  },
>(balances: T[]): T[] {
  const alreadyAdded = new Set<string>();
  return SUPPORTED_GAS_DEPOSIT_CHAIN_ASSETS.reduce((acc, [chain, asset]) => {
    const matches = balances.filter(
      (b) =>
        b.chain === chain &&
        asset === b.assetId &&
        b.assetId !== 'patron_nft' &&
        b.isOfficial === true &&
        !alreadyAdded.has(b.id)
    );
    // Only add a balance to the resulting array once
    matches.forEach((m) => alreadyAdded.add(m.id));
    return [...acc, ...matches];
  }, new Array<T>());
}

export function mapTokenToBalanceWithUSD<T extends TokenBalanceForUsd>(
  balances: T[],
  prices: Record<ChainAddressId, Rate>
): TokenBalanceWithUSD<T>[] {
  return balances.map((balance) => {
    const price: Rate | undefined =
      prices[makeChainAddressId(balance.chain, balance.address)];
    const balanceUsd = price
      ? BigNumber(balance.balance).times(price.usd.rate).toNumber()
      : undefined;

    return {
      ...balance,
      balanceUsd,
      usdRate: price?.usd.rate,
    };
  });
}
